import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};

export const BlogPreview = () => {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles?limit=3`);
        setArticles(data.slice(0, 3));
      } catch {
        /* silently hide the section if the endpoint fails */
      }
    })();
  }, []);

  // Hide the section entirely until there's at least 1 article
  if (articles.length === 0) return null;

  return (
    <section id="blog-preview" className="py-24 sm:py-28 bg-[#07111C] border-y border-slate-900" data-testid="blog-preview">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-4 h-4 text-[#E6B445]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445]">
                From the blog
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[1.02]">
              Guides &amp; <span className="text-[#1E88E5]">field notes.</span>
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="blog-preview-see-all"
            className="self-start sm:self-end inline-flex items-center gap-2 font-heading uppercase text-sm tracking-wider text-slate-300 hover:text-white transition-colors"
          >
            See all articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
            >
              <Link
                to={`/blog/${a.slug}`}
                data-testid={`blog-preview-card-${a.slug}`}
                className="group block bg-[#142238] border border-slate-800 hover:border-[#1E88E5] rounded-md overflow-hidden transition-colors h-full flex flex-col"
              >
                {a.hero_image_url ? (
                  <div className="aspect-video overflow-hidden bg-[#0B1726]">
                    <img
                      src={a.hero_image_url}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-[#0B1726] to-[#142238] flex items-center justify-center">
                    <FileText className="w-8 h-8 text-slate-700" />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 font-heading tracking-widest uppercase mb-2">
                    <Calendar className="w-3 h-3" />
                    <span>{fmtDate(a.published_at)}</span>
                  </div>
                  <h3 className="font-heading text-base sm:text-lg font-bold uppercase tracking-tight text-white leading-tight group-hover:text-[#E6B445] transition-colors">
                    {a.title}
                  </h3>
                  {a.meta_description && (
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2 flex-1">
                      {a.meta_description}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
